import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { FiFilter, FiColumns, FiDownload, FiPlus, FiTrash2, FiEdit2 } from 'react-icons/fi';
import { MdOutlinePushPin, MdPushPin } from 'react-icons/md';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { useAuthFetch } from '../hooks/useAuthFetch';
import Icon13 from '../img/7481d4f175f497fa3ca635d9ea8974d559a00bf54762cdfe73ec49ac971c04f3.svg';

// Import shared components
import { TooltipCell, ResetButton, ColumnVisibilityDropdown, SortIcon, sortIconDirection } from './common';
import { ColumnFilterDropdown, RangeFilterDropdown, DateRangeFilterDropdown, useFilterPopupPortal } from './common/filters';
import RowsIndicator from './common/RowsIndicator';
import { loadXLSX } from '../utils/xlsxLoader';
import { useUserPreference } from '../hooks/useUserPreference';
import { USER_PREFERENCE_KEYS } from '../constants/userPreferences';
import InventoryLedgerModal from './InventoryLedgerModal';
import { ConfirmDialog } from './common/ConfirmDialog';

const DEFAULT_COLUMN_ORDER = [
  'date', 'projectNo', 'projectName', 'activity', 'division', 'branchLocation', 'inventoryId', 'description', 'uom', 'takenToProject', 'returnedFromProject', 'takenToDate', 'totalEstimated', 'percentOnsite', 'dailyNotes', 'branch', 'warehouse', 'projectTask', 'costCode', 'reasonCode', 'location', 'savedByName', 'updatedAtIso', 'actions'
];

const ITEMS_PER_PAGE = 15;
const INITIAL_LOAD_COUNT = 15;

// Helper to format date to MM/DD/YYYY, h:mm:ss am/pm
const formatFullTimestamp = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${mm}/${dd}/${yyyy}, ${hours}:${minutes}:${seconds} ${ampm}`;
};

// Helper to format date as MM/DD/YYYY (used for filter dropdown and Excel export)
const formatMMDDYYYY = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const formatDisplayNumber = (value) => {
  if (value == null || value === '' || value === '--') return '--';
  const parsed = parseFloat(value);
  if (Number.isNaN(parsed)) return String(value);
  return Math.round(parsed).toLocaleString();
};

const formatPercentOnsite = (takenToDate, totalEstimated) => {
  const total = Number(totalEstimated) || 0;
  if (total <= 0) return '--';
  const pct = Math.round((Number(takenToDate) / total) * 100);
  return `${pct}%`;
};

const enrichLedgerRecord = (item) => {
  if (item.takenToDate !== undefined && item.percentOnsite !== undefined) {
    return item;
  }
  const totalTaken = Number(item.takenToProject) || 0;
  const returned = Number(item.returnedFromProject) || 0;
  const netOnsite = Math.max(0, totalTaken - returned);
  const totalEstimatedNum = Number(item.totalEstimatedNum ?? item.totalEstimated) || 0;
  return {
    ...item,
    takenToDate: formatDisplayNumber(netOnsite),
    takenToDateNum: netOnsite,
    totalEstimated: formatDisplayNumber(totalEstimatedNum),
    totalEstimatedNum,
    percentOnsite: formatPercentOnsite(netOnsite, totalEstimatedNum),
  };
};

const INITIAL_INVENTORY = [
  {
    id: 1,
    date: '4/20/2026',
    projectNo: '1-01319',
    projectName: 'McCue Building',
    activity: 'FL - Waterproofing',
    division: 'Floors',
    branchLocation: 'Waller, TX',
    inventoryId: 'IN0135',
    description: 'T-Bar 2"',
    takenToProject: 25,
    returnedFromProject: 0,
    uom: 'EA',
    branch: 'NTCS',
    warehouse: 'HOWH',
    projectTask: 'FL COST',
    costCode: '020-086',
    reasonCode: 'FLPROJECT',
    location: 'STORAGE'
  },
  {
    id: 2,
    date: '4/20/2026',
    projectNo: '1-01319',
    projectName: 'McCue Building',
    activity: 'FL - Waterproofing',
    division: 'Floors',
    branchLocation: 'Waller, TX',
    inventoryId: 'IN0138',
    description: 'Drip Edge',
    takenToProject: 25,
    returnedFromProject: 0,
    uom: 'EA',
    branch: 'NTCS',
    warehouse: 'HOWH',
    projectTask: 'FL COST',
    costCode: '020-085',
    reasonCode: 'FLPROJECT',
    location: 'STORAGE'
  },
  {
    id: 3,
    date: '4/20/2026',
    projectNo: '1-01319',
    projectName: 'McCue Building',
    activity: 'FL - Waterproofing',
    division: 'Floors',
    branchLocation: 'Waller, TX',
    inventoryId: 'IN0374',
    description: '5"x5" flashing',
    takenToProject: 25,
    returnedFromProject: 0,
    uom: 'EA',
    branch: 'NTCS',
    warehouse: 'HOWH',
    projectTask: 'FL COST',
    costCode: '020-085',
    reasonCode: 'FLPROJECT',
    location: 'STORAGE'
  },
  {
    id: 4,
    date: '4/20/2026',
    projectNo: '1-01319',
    projectName: 'McCue Building',
    activity: 'FL - Waterproofing',
    division: 'Floors',
    branchLocation: 'Waller, TX',
    inventoryId: 'IN0105',
    description: 'Polyguard 105-12 Flashing',
    takenToProject: 10,
    returnedFromProject: 0,
    uom: 'ROLL',
    branch: 'NTCS',
    warehouse: 'HOWH',
    projectTask: 'FL COST',
    costCode: '020-121',
    reasonCode: 'FLPROJECT',
    location: 'STORAGE'
  },
  {
    id: 5,
    date: '4/19/2026',
    projectNo: '1-01319',
    projectName: 'McCue Building',
    activity: 'FL - Waterproofing',
    division: 'Floors',
    branchLocation: 'Waller, TX',
    inventoryId: 'IN0138',
    description: 'Drip Edge',
    takenToProject: 75,
    returnedFromProject: 0,
    uom: 'EA',
    branch: 'NTCS',
    warehouse: 'HOWH',
    projectTask: 'FL COST',
    costCode: '020-085',
    reasonCode: 'FLPROJECT',
    location: 'STORAGE'
  },
  // Additional mock rows for variety
  {
    id: 6,
    date: '4/18/2026',
    projectNo: '2-04512',
    projectName: 'Evren Building',
    activity: 'Roof - Waterproofing',
    division: 'Roof Deck',
    branchLocation: 'Houston, TX',
    inventoryId: 'IN0201',
    description: 'Roofing Felt 15#',
    takenToProject: 50,
    returnedFromProject: 5,
    uom: 'ROLL',
    branch: 'NTCS',
    warehouse: 'HOUH',
    projectTask: 'RF COST',
    costCode: '030-012',
    reasonCode: 'RFPROJECT',
    location: 'YARD'
  },
  {
    id: 7,
    date: '4/17/2026',
    projectNo: '2-04512',
    projectName: 'Evren Building',
    activity: 'Doors - Installation',
    division: 'Doors',
    branchLocation: 'Houston, TX',
    inventoryId: 'IN0441',
    description: 'Heavy Duty Hinge 4"',
    takenToProject: 100,
    returnedFromProject: 12,
    uom: 'EA',
    branch: 'NTCS',
    warehouse: 'HOUH',
    projectTask: 'DR COST',
    costCode: '040-022',
    reasonCode: 'DRPROJECT',
    location: 'STORAGE'
  },
  {
    id: 8,
    date: '4/15/2026',
    projectNo: '1-01319',
    projectName: 'McCue Building',
    activity: 'FL - Waterproofing',
    division: 'Floors',
    branchLocation: 'Waller, TX',
    inventoryId: 'IN0029',
    description: 'Silicone Sealant',
    takenToProject: 40,
    returnedFromProject: 2,
    uom: 'TUBE',
    branch: 'NTCS',
    warehouse: 'HOWH',
    projectTask: 'FL COST',
    costCode: '020-090',
    reasonCode: 'FLPROJECT',
    location: 'STORAGE'
  }
];

const DEFAULT_VISIBLE_COLUMNS = [
  'date', 'projectNo', 'projectName', 'activity', 'division', 'description', 'uom', 'takenToDate', 'totalEstimated'
];

function mergeColumnOrder(savedOrder) {
  if (!Array.isArray(savedOrder)) {
    return DEFAULT_COLUMN_ORDER;
  }

  const merged = [...savedOrder];
  DEFAULT_COLUMN_ORDER.forEach((key) => {
    if (!merged.includes(key)) {
      merged.push(key);
    }
  });
  return merged.filter((key) => DEFAULT_COLUMN_ORDER.includes(key));
}

function mergeVisibleColumns(savedColumns) {
  if (!Array.isArray(savedColumns) || savedColumns.length === 0) {
    return DEFAULT_VISIBLE_COLUMNS;
  }
  // Match Projects/Contacts: keep exactly what the user saved (minus invalid/actions keys).
  // Do NOT re-add DEFAULT_VISIBLE_COLUMNS here — that resets hide/show after refresh.
  return savedColumns.filter(
    (key) => key !== 'actions' && DEFAULT_COLUMN_ORDER.includes(key)
  );
}

const DEFAULT_PINNED_COLUMNS = [];

function mergePinnedColumns(savedPins) {
  if (!Array.isArray(savedPins)) {
    return DEFAULT_PINNED_COLUMNS;
  }
  return savedPins.filter(
    (key) => key !== 'actions' && DEFAULT_COLUMN_ORDER.includes(key)
  );
}

function InventoryManagement() {
  const { accounts } = useMsal();
  const { getAuthConfig, isReady } = useAuthFetch();
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    value: visibleColumns,
    setValue: setVisibleColumns,
    save: saveVisibleColumns,
  } = useUserPreference(
    USER_PREFERENCE_KEYS.INVENTORY_VISIBLE_COLUMNS,
    DEFAULT_VISIBLE_COLUMNS,
    {
      mergeWithDefault: mergeVisibleColumns,
      localStorageKey: 'inventory_visibleColumns',
    }
  );

  const {
    value: columnOrder,
    setValue: setColumnOrder,
    save: saveColumnOrder,
  } = useUserPreference(
    USER_PREFERENCE_KEYS.INVENTORY_COLUMN_ORDER,
    DEFAULT_COLUMN_ORDER,
    {
      mergeWithDefault: mergeColumnOrder,
      localStorageKey: 'inventory_columnOrder',
    }
  );

  const {
    value: pinnedColumns,
    setValue: setPinnedColumns,
    save: savePinnedColumns,
  } = useUserPreference(
    USER_PREFERENCE_KEYS.INVENTORY_PINNED_COLUMNS,
    DEFAULT_PINNED_COLUMNS,
    {
      mergeWithDefault: mergePinnedColumns,
      localStorageKey: 'inventory_pinnedColumns',
    }
  );

  // Filters state
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem('inventory_searchTerm') || '');

  const [columnFilters, setColumnFilters] = useState(() => {
    const saved = localStorage.getItem('inventory_columnFilters');
    return saved ? JSON.parse(saved) : {
      projectNo: '',
      projectName: '',
      inventoryId: '',
      description: '',
      savedByName: '',
      dailyNotes: ''
    };
  });

  const [columnFilterValues, setColumnFilterValues] = useState(() => {
    const saved = localStorage.getItem('inventory_columnFilterValues');
    return saved ? JSON.parse(saved) : {
      activity: [],
      division: [],
      branchLocation: [],
      uom: [],
      warehouse: [],
      location: [],
      branch: [],
      projectTask: [],
      costCode: [],
      reasonCode: [],
      lastModifiedDate: [],
      percentOnsite: []
    };
  });

  const [dateRange, setDateRange] = useState(() => {
    const saved = localStorage.getItem('inventory_dateRange');
    return saved ? JSON.parse(saved) : { start: null, end: null };
  });

  const [takenToProjectRange, setTakenToProjectRange] = useState(() => {
    const saved = localStorage.getItem('inventory_takenRange');
    return saved ? JSON.parse(saved) : { min: null, max: null };
  });

  const [returnedFromProjectRange, setReturnedFromProjectRange] = useState(() => {
    const saved = localStorage.getItem('inventory_returnedRange');
    return saved ? JSON.parse(saved) : { min: null, max: null };
  });

  const [takenToDateRange, setTakenToDateRange] = useState(() => {
    const saved = localStorage.getItem('inventory_takenToDateRange');
    return saved ? JSON.parse(saved) : { min: null, max: null };
  });

  const [totalEstimatedRange, setTotalEstimatedRange] = useState(() => {
    const saved = localStorage.getItem('inventory_totalEstimatedRange');
    return saved ? JSON.parse(saved) : { min: null, max: null };
  });

  const [sortConfig, setSortConfig] = useState(() => {
    const saved = localStorage.getItem('inventory_sortConfig');
    return saved ? JSON.parse(saved) : { key: 'date', direction: 'desc' };
  });

  const [selectedRowId, setSelectedRowId] = useState(null);
  const [currentDisplayCount, setCurrentDisplayCount] = useState(INITIAL_LOAD_COUNT);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchInventoryLedger = async (silent = false) => {
    if (!isReady) return;
    try {
      if (!silent) setLoading(true);
      setError(null);
      const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
      let allRecords = [];
      let offset = 0;
      const limit = 100;
      let hasMore = true;

      const fetchPage = async (pageOffset) => {
        const maxAttempts = 3;
        let lastError;

        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          try {
            return await axios.get(
              `${baseUrl}/api/miter/production-performance/inventory-ledger`,
              await getAuthConfig({ params: { limit, offset: pageOffset } })
            );
          } catch (err) {
            lastError = err;
            const message = err.response?.data?.message || err.message || '';
            const isTransient = /connection terminated|connection ended|ECONNRESET/i.test(message);
            if (!isTransient || attempt === maxAttempts - 1) {
              throw err;
            }
            await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
          }
        }

        throw lastError;
      };

      while (hasMore) {
        const response = await fetchPage(offset);

        if (response.data && response.data.status === 'success') {
          const records = response.data.data?.records || [];
          allRecords = [...allRecords, ...records];
          hasMore = response.data.data?.pagination?.hasMore || false;
          offset += limit;
        } else {
          throw new Error(response.data?.message || 'Failed to fetch inventory ledger data');
        }

        // Safety break
        if (limit <= 0 || allRecords.length > 10000) {
          break;
        }
      }

      setAllData(allRecords.map(enrichLedgerRecord));
    } catch (err) {
      console.error('Error fetching inventory ledger:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch inventory ledger data');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (isReady) {
      fetchInventoryLedger();
    }
  }, [isReady]);

  // Dropdown UI states
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
  const [columnSelectorPosition, setColumnSelectorPosition] = useState({ top: 0, left: 0 });
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const columnButtonRef = useRef(null);
  const columnSelectorRef = useRef(null);
  const loadingRef = useRef(null);
  const tableScrollRef = useRef(null);
  const hScrollbarRef = useRef(null);
  const {
    filterDropdownRef,
    toggleFilterPopup,
    closeFilterPopup,
    renderFilterPopup,
    getFilterWrapperStyle,
  } = useFilterPopupPortal({
    activeFilterColumn,
    setActiveFilterColumn,
    tableScrollRef,
    hScrollbarRef,
  });
  const hScrollLastRef = useRef({ overflow: 0, scrollWidth: 0, clientWidth: 0 });
  const [hScroll, setHScroll] = useState({ overflow: 0, scrollWidth: 0, clientWidth: 0 });

  // Columns definition — px widths (same pattern as Projects/Contacts/Accounts)
  const originalColumns = useMemo(() => [
    { key: 'date', label: 'Date', width: 120, filterKey: 'date', filterable: true, type: 'date' },
    { key: 'projectNo', label: 'Project No.', width: 120, filterKey: 'projectNo', filterable: true, type: 'search' },
    { key: 'projectName', label: 'Project Name', width: 220, filterKey: 'projectName', filterable: true, type: 'search' },
    { key: 'activity', label: 'Activity', width: 180, filterKey: 'activity', filterable: true, type: 'select' },
    { key: 'division', label: 'Division', width: 120, filterKey: 'division', filterable: true, type: 'select' },
    { key: 'branchLocation', label: 'Branch (Location)', width: 140, filterKey: 'branchLocation', filterable: true, type: 'select' },
    { key: 'inventoryId', label: 'Inventory ID', width: 120, filterKey: 'inventoryId', filterable: true, type: 'search' },
    { key: 'description', label: 'Description', width: 200, filterKey: 'description', filterable: true, type: 'search' },
    { key: 'uom', label: 'UOM', width: 80, filterKey: 'uom', filterable: true, type: 'select' },
    { key: 'takenToProject', label: 'Taken', width: 100, filterKey: 'takenToProject', filterable: true, type: 'range' },
    { key: 'returnedFromProject', label: 'Returned', width: 100, filterKey: 'returnedFromProject', filterable: true, type: 'range' },
    { key: 'takenToDate', label: 'Taken To Date', width: 120, filterKey: 'takenToDate', filterable: true, type: 'range' },
    { key: 'totalEstimated', label: 'Total Estimated', width: 130, filterKey: 'totalEstimated', filterable: true, type: 'range' },
    { key: 'percentOnsite', label: '% Onsite', width: 100, filterKey: 'percentOnsite', filterable: true, type: 'select' },
    { key: 'dailyNotes', label: 'Daily Notes', width: 200, filterKey: 'dailyNotes', filterable: true, type: 'search' },
    { key: 'branch', label: 'Branch', width: 100, filterKey: 'branch', filterable: true, type: 'select' },
    { key: 'warehouse', label: 'Warehouse', width: 120, filterKey: 'warehouse', filterable: true, type: 'select' },
    { key: 'projectTask', label: 'Project Task', width: 120, filterKey: 'projectTask', filterable: true, type: 'select' },
    { key: 'costCode', label: 'Cost Code', width: 120, filterKey: 'costCode', filterable: true, type: 'select' },
    { key: 'reasonCode', label: 'Reason Code', width: 130, filterKey: 'reasonCode', filterable: true, type: 'select' },
    { key: 'location', label: 'Location', width: 120, filterKey: 'location', filterable: true, type: 'select' },
    { key: 'savedByName', label: 'Last Modified By', width: 160, filterKey: 'savedByName', filterable: true, type: 'search' },
    { key: 'updatedAtIso', label: 'Last Modified Date', width: 180, filterKey: 'lastModifiedDate', filterable: true, type: 'select' },
    { key: 'actions', label: 'Actions', width: 100, filterable: false, type: 'actions' },
  ], []);

  const columns = useMemo(() => {
    return [...originalColumns].sort((a, b) => {
      const aIdx = columnOrder.indexOf(a.key);
      const bIdx = columnOrder.indexOf(b.key);
      return (aIdx !== -1 ? aIdx : 99) - (bIdx !== -1 ? bIdx : 99);
    });
  }, [originalColumns, columnOrder]);

  const pinnedVisibleKeys = pinnedColumns.filter((key) => visibleColumns.includes(key));
  const unpinnedVisibleColumns = columns.filter(
    (column) =>
      column.key !== 'actions' &&
      visibleColumns.includes(column.key) &&
      !pinnedVisibleKeys.includes(column.key)
  );
  const actionsCol = columns.find((column) => column.key === 'actions');
  const visibleTableColumns = [
    ...pinnedVisibleKeys
      .map((key) => originalColumns.find((column) => column.key === key))
      .filter(Boolean),
    ...unpinnedVisibleColumns,
    ...(actionsCol ? [actionsCol] : []),
  ];
  const tableMinWidth = visibleTableColumns.reduce((sum, column) => sum + column.width, 0) || 1;
  const lastPinnedKey = pinnedVisibleKeys[pinnedVisibleKeys.length - 1] || null;

  const getPinnedLeftOffset = (columnKey) => {
    let left = 0;
    for (const key of pinnedVisibleKeys) {
      if (key === columnKey) return left;
      const col = originalColumns.find((c) => c.key === key);
      left += col?.width || 0;
    }
    return 0;
  };

  const pinnedColumnsWidth = pinnedVisibleKeys.reduce((sum, key) => {
    const col = originalColumns.find((c) => c.key === key);
    return sum + (col?.width || 0);
  }, 0);

  const getColumnStyle = (column, { isHeader = false } = {}) => {
    const isPinned = pinnedVisibleKeys.includes(column.key);
    if (isPinned) {
      return {
        width: `${column.width}px`,
        minWidth: `${column.width}px`,
        maxWidth: `${column.width}px`,
        position: 'sticky',
        left: `${getPinnedLeftOffset(column.key)}px`,
        zIndex: isHeader ? 30 : 4,
      };
    }
    return {
      minWidth: `${column.width}px`,
      width: `${(column.width / tableMinWidth) * 100}%`,
    };
  };

  // Save data to localStorage
  useEffect(() => {
    localStorage.setItem('inventory_ledger_data', JSON.stringify(allData));
  }, [allData]);

  useEffect(() => {
    localStorage.setItem('inventory_searchTerm', searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    localStorage.setItem('inventory_columnFilters', JSON.stringify(columnFilters));
  }, [columnFilters]);

  useEffect(() => {
    localStorage.setItem('inventory_columnFilterValues', JSON.stringify(columnFilterValues));
  }, [columnFilterValues]);

  useEffect(() => {
    localStorage.setItem('inventory_dateRange', JSON.stringify(dateRange));
  }, [dateRange]);

  useEffect(() => {
    localStorage.setItem('inventory_takenRange', JSON.stringify(takenToProjectRange));
  }, [takenToProjectRange]);

  useEffect(() => {
    localStorage.setItem('inventory_returnedRange', JSON.stringify(returnedFromProjectRange));
  }, [returnedFromProjectRange]);

  useEffect(() => {
    localStorage.setItem('inventory_takenToDateRange', JSON.stringify(takenToDateRange));
  }, [takenToDateRange]);

  useEffect(() => {
    localStorage.setItem('inventory_totalEstimatedRange', JSON.stringify(totalEstimatedRange));
  }, [totalEstimatedRange]);

  useEffect(() => {
    localStorage.setItem('inventory_sortConfig', JSON.stringify(sortConfig));
  }, [sortConfig]);

  // Click outside listener for columns dropdown
  useEffect(() => {
    if (!isColumnSelectorOpen) return;
    function handleClickOutside(event) {
      if (columnSelectorRef.current && !columnSelectorRef.current.contains(event.target)) {
        setIsColumnSelectorOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isColumnSelectorOpen]);

  // Position column selector button
  const handleColumnSelectorClick = () => {
    setIsColumnSelectorOpen(prev => !prev);
  };

  // Update dropdown position when it opens (matches Projects page)
  useEffect(() => {
    if (isColumnSelectorOpen && columnButtonRef.current) {
      const updatePosition = () => {
        if (columnButtonRef.current) {
          const rect = columnButtonRef.current.getBoundingClientRect();
          const dropdownWidth = 192;
          let left = rect.left;
          let top = rect.bottom + 4;
          if (left + dropdownWidth > window.innerWidth - 8) {
            left = rect.right - dropdownWidth;
          }
          if (left < 8) left = 8;
          setColumnSelectorPosition({ top, left });
        }
      };

      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isColumnSelectorOpen]);

  const handleToggleColumn = (columnKey) => {
    if (columnKey === 'actions') return;
    const isHiding = visibleColumns.includes(columnKey);
    const newCols = isHiding
      ? visibleColumns.filter((key) => key !== columnKey)
      : [...visibleColumns, columnKey];
    setVisibleColumns(newCols);
    saveVisibleColumns(newCols);

    if (isHiding && pinnedColumns.includes(columnKey)) {
      const nextPins = pinnedColumns.filter((key) => key !== columnKey);
      setPinnedColumns(nextPins);
      savePinnedColumns(nextPins);
    }
  };

  const handleTogglePin = (columnKey) => {
    if (columnKey === 'actions') return;

    if (pinnedColumns.includes(columnKey)) {
      const nextPins = pinnedColumns.filter((key) => key !== columnKey);
      setPinnedColumns(nextPins);
      savePinnedColumns(nextPins);
      return;
    }

    if (!visibleColumns.includes(columnKey)) {
      const nextVisible = [...visibleColumns, columnKey];
      setVisibleColumns(nextVisible);
      saveVisibleColumns(nextVisible);
    }

    const nextPins = [...pinnedColumns, columnKey];
    setPinnedColumns(nextPins);
    savePinnedColumns(nextPins);
  };

  const toggleColumnFilter = (columnName, event) => {
    toggleFilterPopup(columnName, event);
  };

  const handleColumnFilterSelect = (columnName, value) => {
    setColumnFilterValues(prev => ({
      ...prev,
      [columnName]: prev[columnName]?.includes(value)
        ? prev[columnName].filter(v => v !== value)
        : [...(prev[columnName] || []), value]
    }));
    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
    setHasMoreData(true);
  };

  const draggedHeaderKeyRef = useRef(null);
  const draggedHeaderWasPinnedRef = useRef(false);
  const pendingColumnOrderRef = useRef(null);
  const pendingPinnedColumnsRef = useRef(null);

  const handleReorderColumn = (fromIndex, toIndex, groupColumns) => {
    const fromKey = groupColumns[fromIndex].key;
    const toKey = groupColumns[toIndex].key;
    if (fromKey === 'actions' || toKey === 'actions') return;

    setColumnOrder((prevOrder) => {
      const newOrder = [...prevOrder];
      const idxA = newOrder.indexOf(fromKey);
      const idxB = newOrder.indexOf(toKey);
      if (idxA !== -1 && idxB !== -1) {
        const [movedItem] = newOrder.splice(idxA, 1);
        newOrder.splice(idxB, 0, movedItem);
      }
      // Keep ref in sync for dragEnd save (same tick as last dragOver).
      pendingColumnOrderRef.current = newOrder;
      return newOrder;
    });
  };

  const handleReorderColumnEnd = () => {
    const orderToSave = pendingColumnOrderRef.current;
    pendingColumnOrderRef.current = null;
    if (orderToSave) {
      saveColumnOrder(orderToSave);
    } else {
      saveColumnOrder();
    }
  };

  const handleHeaderDragStart = (e, columnKey) => {
    if (columnKey === 'actions') return;
    if (e.target.closest?.('[data-pin-toggle]')) {
      e.preventDefault();
      return;
    }
    draggedHeaderKeyRef.current = columnKey;
    draggedHeaderWasPinnedRef.current = pinnedColumns.includes(columnKey);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', columnKey);
  };

  const handleHeaderDragOver = (e, targetKey) => {
    e.preventDefault();
    const draggedKey = draggedHeaderKeyRef.current;
    if (!draggedKey || draggedKey === targetKey || targetKey === 'actions') return;

    const draggedPinned = pinnedColumns.includes(draggedKey);
    const targetPinned = pinnedColumns.includes(targetKey);

    if (draggedPinned && targetPinned) {
      setPinnedColumns((prev) => {
        const next = [...prev];
        const idxA = next.indexOf(draggedKey);
        const idxB = next.indexOf(targetKey);
        if (idxA === -1 || idxB === -1) return prev;
        const [movedItem] = next.splice(idxA, 1);
        next.splice(idxB, 0, movedItem);
        pendingPinnedColumnsRef.current = next;
        return next;
      });
      return;
    }

    if (!draggedPinned && !targetPinned) {
      setColumnOrder((prevOrder) => {
        const newOrder = [...prevOrder];
        const idxA = newOrder.indexOf(draggedKey);
        const idxB = newOrder.indexOf(targetKey);
        if (idxA !== -1 && idxB !== -1) {
          const [movedItem] = newOrder.splice(idxA, 1);
          newOrder.splice(idxB, 0, movedItem);
        }
        pendingColumnOrderRef.current = newOrder;
        return newOrder;
      });
    }
  };

  const handleHeaderDragEnd = () => {
    const wasPinned = draggedHeaderWasPinnedRef.current;
    draggedHeaderKeyRef.current = null;
    draggedHeaderWasPinnedRef.current = false;

    if (wasPinned) {
      const pinsToSave = pendingPinnedColumnsRef.current;
      pendingPinnedColumnsRef.current = null;
      if (pinsToSave) {
        savePinnedColumns(pinsToSave);
      } else {
        savePinnedColumns();
      }
      return;
    }

    const orderToSave = pendingColumnOrderRef.current;
    pendingColumnOrderRef.current = null;
    if (orderToSave) {
      saveColumnOrder(orderToSave);
    } else {
      saveColumnOrder();
    }
  };

  // Reset filtering data and column preferences
  const handleResetData = async () => {
    setSearchTerm('');
    setColumnFilters({
      projectNo: '',
      projectName: '',
      inventoryId: '',
      description: '',
      savedByName: '',
      dailyNotes: ''
    });
    setColumnFilterValues({
      activity: [],
      division: [],
      branchLocation: [],
      uom: [],
      warehouse: [],
      location: [],
      branch: [],
      projectTask: [],
      costCode: [],
      reasonCode: [],
      lastModifiedDate: [],
      percentOnsite: []
    });
    setDateRange({ start: null, end: null });
    setTakenToProjectRange({ min: null, max: null });
    setReturnedFromProjectRange({ min: null, max: null });
    setTakenToDateRange({ min: null, max: null });
    setTotalEstimatedRange({ min: null, max: null });
    setSortConfig({ key: 'date', direction: 'desc' });
    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
    setHasMoreData(true);
    setLoadingMore(false);

    localStorage.removeItem('inventory_columnOrder');
    localStorage.removeItem('inventory_visibleColumns');
    localStorage.removeItem('inventory_pinnedColumns');
    localStorage.removeItem('inventory_takenToDateRange');
    localStorage.removeItem('inventory_totalEstimatedRange');

    setColumnOrder(DEFAULT_COLUMN_ORDER);
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    setPinnedColumns(DEFAULT_PINNED_COLUMNS);

    void Promise.all([
      saveColumnOrder(DEFAULT_COLUMN_ORDER),
      saveVisibleColumns(DEFAULT_VISIBLE_COLUMNS),
      savePinnedColumns(DEFAULT_PINNED_COLUMNS),
    ]);

    await fetchInventoryLedger();
  };

  // Sorting helper
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
    setHasMoreData(true);
  };

  // Extract unique values for filter options
  const getUniqueValues = (key) => {
    let values;
    if (key === 'updatedAtIso') {
      values = allData.map(item => formatMMDDYYYY(item[key])).filter(Boolean);
    } else {
      values = allData.map(item => item[key]).filter(Boolean);
    }
    return [...new Set(values)].sort().map(val => ({
      value: val,
      label: val
    }));
  };

  // Filtering and sorting logic
  const getFilteredAndSortedData = () => {
    let filtered = [...allData];

    // Global Search
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        return Object.values(item).some(val =>
          val && val.toString().toLowerCase().includes(query)
        );
      });
    }

    // Column Text Searches
    if (columnFilters.projectNo) {
      const query = columnFilters.projectNo.toLowerCase();
      filtered = filtered.filter(item => item.projectNo?.toLowerCase().includes(query));
    }
    if (columnFilters.projectName) {
      const query = columnFilters.projectName.toLowerCase();
      filtered = filtered.filter(item => item.projectName?.toLowerCase().includes(query));
    }
    if (columnFilters.inventoryId) {
      const query = columnFilters.inventoryId.toLowerCase();
      filtered = filtered.filter(item => item.inventoryId?.toLowerCase().includes(query));
    }
    if (columnFilters.description) {
      const query = columnFilters.description.toLowerCase();
      filtered = filtered.filter(item => item.description?.toLowerCase().includes(query));
    }
    if (columnFilters.savedByName) {
      const query = columnFilters.savedByName.toLowerCase();
      filtered = filtered.filter(item => item.savedByName?.toLowerCase().includes(query));
    }
    if (columnFilters.dailyNotes) {
      const query = columnFilters.dailyNotes.toLowerCase();
      filtered = filtered.filter(item => item.dailyNotes?.toLowerCase().includes(query));
    }

    // Multi-select column filter values
    Object.keys(columnFilterValues).forEach(key => {
      const selected = columnFilterValues[key];
      if (selected && selected.length > 0) {
        if (key === 'lastModifiedDate') {
          filtered = filtered.filter(item => {
            const formattedDate = formatMMDDYYYY(item.updatedAtIso);
            return selected.includes(formattedDate);
          });
        } else {
          filtered = filtered.filter(item => selected.includes(item[key]));
        }
      }
    });

    // Date range filter
    if (dateRange.start || dateRange.end) {
      filtered = filtered.filter(item => {
        if (!item.date) return false;
        const itemTime = new Date(item.date).getTime();
        if (dateRange.start && new Date(dateRange.start).getTime() > itemTime) return false;
        if (dateRange.end && new Date(dateRange.end).getTime() < itemTime) return false;
        return true;
      });
    }

    // Numeric ranges
    if (takenToProjectRange.min !== null) {
      filtered = filtered.filter(item => (Number(item.takenToProject) || 0) >= Number(takenToProjectRange.min));
    }
    if (takenToProjectRange.max !== null) {
      filtered = filtered.filter(item => (Number(item.takenToProject) || 0) <= Number(takenToProjectRange.max));
    }
    if (returnedFromProjectRange.min !== null) {
      filtered = filtered.filter(item => (Number(item.returnedFromProject) || 0) >= Number(returnedFromProjectRange.min));
    }
    if (returnedFromProjectRange.max !== null) {
      filtered = filtered.filter(item => (Number(item.returnedFromProject) || 0) <= Number(returnedFromProjectRange.max));
    }
    if (takenToDateRange.min !== null) {
      filtered = filtered.filter(item => (Number(item.takenToDateNum ?? item.takenToDate) || 0) >= Number(takenToDateRange.min));
    }
    if (takenToDateRange.max !== null) {
      filtered = filtered.filter(item => (Number(item.takenToDateNum ?? item.takenToDate) || 0) <= Number(takenToDateRange.max));
    }
    if (totalEstimatedRange.min !== null) {
      filtered = filtered.filter(item => (Number(item.totalEstimatedNum ?? item.totalEstimated) || 0) >= Number(totalEstimatedRange.min));
    }
    if (totalEstimatedRange.max !== null) {
      filtered = filtered.filter(item => (Number(item.totalEstimatedNum ?? item.totalEstimated) || 0) <= Number(totalEstimatedRange.max));
    }

    // Apply Sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle numeric fields
        if (['takenToProject', 'returnedFromProject', 'takenToDate', 'totalEstimated'].includes(sortConfig.key)) {
          if (sortConfig.key === 'takenToDate') {
            aVal = Number(a.takenToDateNum ?? a.takenToDate) || 0;
            bVal = Number(b.takenToDateNum ?? b.takenToDate) || 0;
          } else if (sortConfig.key === 'totalEstimated') {
            aVal = Number(a.totalEstimatedNum ?? a.totalEstimated) || 0;
            bVal = Number(b.totalEstimatedNum ?? b.totalEstimated) || 0;
          } else {
            aVal = Number(aVal) || 0;
            bVal = Number(bVal) || 0;
          }
        } else if (sortConfig.key === 'percentOnsite') {
          const parsePercent = (val) => {
            if (!val || val === '--') return -1;
            return Number(String(val).replace('%', '')) || 0;
          };
          aVal = parsePercent(a.percentOnsite);
          bVal = parsePercent(b.percentOnsite);
        } else if (['date', 'updatedAtIso'].includes(sortConfig.key)) {
          aVal = new Date(aVal).getTime() || 0;
          bVal = new Date(bVal).getTime() || 0;
        } else {
          aVal = (aVal || '').toString().toLowerCase();
          bVal = (bVal || '').toString().toLowerCase();
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  };

  const filteredAndSortedData = useMemo(() => getFilteredAndSortedData(), [
    allData, searchTerm, columnFilters, columnFilterValues, dateRange, takenToProjectRange, returnedFromProjectRange, takenToDateRange, totalEstimatedRange, sortConfig
  ]);

  const displayedData = useMemo(() => {
    const sliced = filteredAndSortedData.slice(0, currentDisplayCount);
    const hasMore = currentDisplayCount < filteredAndSortedData.length;
    if (hasMoreData !== hasMore) {
      setHasMoreData(hasMore);
    }
    return sliced;
  }, [filteredAndSortedData, currentDisplayCount, hasMoreData]);

  // Infinite scroll (same UX as Projects/Contacts/Accounts — spinner while paging)
  const loadMoreData = React.useCallback(() => {
    if (loadingMore || !hasMoreData || loading) return;

    setLoadingMore(true);
    requestAnimationFrame(() => {
      setTimeout(() => {
        setCurrentDisplayCount((prev) => {
          const newCount = Math.min(prev + ITEMS_PER_PAGE, filteredAndSortedData.length);
          setHasMoreData(newCount < filteredAndSortedData.length);
          return newCount;
        });
        setLoadingMore(false);
      }, 300);
    });
  }, [loadingMore, hasMoreData, loading, filteredAndSortedData.length]);

  useEffect(() => {
    if (loading || loadingMore || !hasMoreData) return;

    const root = tableScrollRef.current;
    const sentinel = loadingRef.current;
    if (!root || !sentinel) return;

    let cancelled = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (cancelled) return;
        if (entries[0]?.isIntersecting) {
          loadMoreData();
        }
      },
      { root, threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [hasMoreData, loading, loadingMore, currentDisplayCount, loadMoreData]);

  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;
    const measure = () => {
      const clientWidth = el.clientWidth;
      const scrollWidth = el.scrollWidth;
      const overflow = Math.max(0, scrollWidth - clientWidth);
      const last = hScrollLastRef.current;
      if (
        Math.abs(overflow - last.overflow) < 1 &&
        Math.abs(scrollWidth - last.scrollWidth) < 1 &&
        Math.abs(clientWidth - last.clientWidth) < 1
      ) {
        return;
      }
      hScrollLastRef.current = { overflow, scrollWidth, clientWidth };
      setHScroll({ overflow, scrollWidth, clientWidth });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tableMinWidth, pinnedColumnsWidth, loading, displayedData.length]);

  const handleTableScroll = (event) => {
    const bar = hScrollbarRef.current;
    if (!bar) return;
    const next = event.currentTarget.scrollLeft;
    if (Math.abs(bar.scrollLeft - next) >= 1) bar.scrollLeft = next;
  };

  const handleHScrollbarScroll = (event) => {
    const scroller = tableScrollRef.current;
    if (!scroller) return;
    const next = event.currentTarget.scrollLeft;
    if (Math.abs(scroller.scrollLeft - next) >= 1) scroller.scrollLeft = next;
  };

  const handleTableWheelCapture = (event) => {
    if (!pinnedColumnsWidth) return;
    const pinnedEdgeEl = event.currentTarget.querySelector('.projects-col-pinned-last');
    if (!pinnedEdgeEl) return;
    const pinnedRight = pinnedEdgeEl.getBoundingClientRect().right;
    if (event.clientX > pinnedRight) return;
    const isHorizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey;
    if (!isHorizontal) return;
    event.preventDefault();
    event.stopPropagation();
  };

  const totalCount = filteredAndSortedData.length;

  // Excel Exporter
  const handleExportToExcel = async () => {
    const XLSX = await loadXLSX();
    if (!XLSX) {
      console.error('XLSX library not loaded');
      return;
    }
    const exportColumns = columns.filter(c => visibleColumns.includes(c.key));
    const headers = exportColumns.map(c => c.label);
    const rows = filteredAndSortedData.map(item =>
      exportColumns.map(c => {
        if (c.key === 'updatedAtIso') {
          const isCreatedSameAsUpdated = item.createdAtIso && item.updatedAtIso && (item.createdAtIso === item.updatedAtIso);
          const isSavedByNameEmpty = !item.savedByName || !item.savedByName.trim();
          if (isCreatedSameAsUpdated && isSavedByNameEmpty) {
            return '-';
          }
          return item.updatedAtIso ? formatMMDDYYYY(item.updatedAtIso) : '-';
        }
        return item[c.key] !== undefined && item[c.key] !== null ? item[c.key] : '-';
      })
    );

    const sheetData = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook => {
      // Stub workbook structure
    });
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory Ledger');
    XLSX.writeFile(wb, 'inventory_ledger.xlsx');
  };

  // Open modal for add
  const handleOpenAddModal = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  // Open modal for edit
  const handleOpenEditModal = (item, e) => {
    e.stopPropagation();
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  const handleOpenDeleteConfirm = (id, e) => {
    e.stopPropagation();
    setDeleteConfirm({ isOpen: true, id });
  };

  const handleCloseDeleteConfirm = () => {
    if (isDeleting) return;
    setDeleteConfirm({ isOpen: false, id: null });
  };

  const handleConfirmDelete = async () => {
    const { id } = deleteConfirm;
    if (!id) return;

    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    setIsDeleting(true);
    try {
      const response = await axios.delete(
        `${baseUrl}/api/miter/production-performance/inventory-ledger/${id}`,
        await getAuthConfig()
      );
      if (response.data && (response.data.status === 'success' || response.status === 200)) {
        if (selectedRowId === id) setSelectedRowId(null);
        setDeleteConfirm({ isOpen: false, id: null });
        await fetchInventoryLedger(true);
      } else {
        throw new Error(response.data?.message || 'Failed to delete ledger entry');
      }
    } catch (err) {
      console.error('Error deleting ledger entry:', err);
      alert(err.response?.data?.message || err.message || 'Failed to delete ledger entry');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="projects-page-card">
      <div className="projects-table-toolbar">
        <div className="projects-table-toolbar-row flex items-center gap-4 max-md:gap-2">
          <form className="projects-toolbar-search flex gap-2.5 items-center px-5 py-3 text-lg font-light bg-white border rounded-[31px] text-zinc-500 w-[30%] shadow-sm max-md:flex-1 max-md:w-auto max-md:bg-stone-50 max-md:border-0 max-md:rounded-full max-md:shadow-none">
            <img
              loading="lazy"
              src={Icon13}
              className="object-contain shrink-0 self-stretch my-auto aspect-square w-[27px]"
              alt=""
            />
            <input
              type="search"
              placeholder="Search"
              className="bg-transparent border-none outline-none w-full"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentDisplayCount(INITIAL_LOAD_COUNT);
                setHasMoreData(true);
              }}
            />
          </form>
          <button
            onClick={handleOpenAddModal}
            className="ml-2 px-6 py-2 rounded transition-colors max-md:hidden bg-red-700 text-white hover:bg-red-800"
          >
            + Add Transaction
          </button>
          <button
            onClick={handleOpenAddModal}
            aria-label="Add Transaction"
            className="projects-toolbar-add hidden max-md:flex items-center justify-center rounded-full w-10 h-10 bg-red-700 text-white"
          >
            <FiPlus className="w-5 h-5" />
          </button>

          <div className="flex-grow max-md:hidden"></div>

          <div className="projects-toolbar-actions">
            <div className="relative projects-toolbar-columns">
              <button
                type="button"
                ref={columnButtonRef}
                onClick={handleColumnSelectorClick}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 max-md:border-0 max-md:bg-transparent max-md:rounded-none max-md:p-2 max-md:hover:bg-transparent max-md:shadow-none"
                title="Show/Hide Columns"
              >
                <FiColumns className="h-4 w-4" />
                <span className="ml-2 max-md:hidden">Columns</span>
              </button>
              <ColumnVisibilityDropdown
                ref={columnSelectorRef}
                isOpen={isColumnSelectorOpen}
                onClose={() => setIsColumnSelectorOpen(false)}
                columns={columns.filter((col) => col.key !== 'actions')}
                visibleColumns={visibleColumns}
                onToggleColumn={handleToggleColumn}
                onReorderColumn={handleReorderColumn}
                onReorderEnd={handleReorderColumnEnd}
                position={columnSelectorPosition}
              />
            </div>

            <button
              type="button"
              onClick={handleExportToExcel}
              disabled={totalCount === 0}
              className={`projects-toolbar-export flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 mr-2 max-md:border-0 max-md:bg-transparent max-md:rounded-none max-md:p-2 max-md:hover:bg-transparent max-md:shadow-none max-md:mr-0 ${totalCount === 0 ? 'cursor-not-allowed opacity-60' : ''
                }`}
              title="Download Excel"
            >
              <FiDownload className="h-4 w-4" />
            </button>

            <ResetButton onClick={handleResetData} tooltip="Reset" />
          </div>
        </div>
      </div>

      <div
        ref={tableScrollRef}
        className="projects-table-scroll projects-table-scroll--proxy-h"
        onScroll={handleTableScroll}
        onWheelCapture={handleTableWheelCapture}
      >
        <table
          className="projects-data-table"
          style={{
            width: `max(100%, ${tableMinWidth}px)`,
            minWidth: `${tableMinWidth}px`,
          }}
        >
          <colgroup>
            {visibleTableColumns.map((column) => {
              const isPinned = pinnedVisibleKeys.includes(column.key);
              return (
                <col
                  key={column.key}
                  style={
                    isPinned
                      ? {
                          width: `${column.width}px`,
                          minWidth: `${column.width}px`,
                        }
                      : {
                          width: `${(column.width / tableMinWidth) * 100}%`,
                          minWidth: `${column.width}px`,
                        }
                  }
                />
              );
            })}
          </colgroup>
          <thead className="bg-gray-50">
            <tr>
              {visibleTableColumns.map((column) => {
                const isPinned = pinnedVisibleKeys.includes(column.key);
                return (
                  <th
                    key={column.key}
                    style={getColumnStyle(column, { isHeader: true })}
                    className={`group/header px-4 py-3 max-md:whitespace-normal ${column.key !== 'actions' ? 'cursor-grab active:cursor-grabbing' : ''} transition-colors hover:bg-gray-100 ${
                      isPinned ? 'projects-col-pinned' : ''
                    } ${column.key === lastPinnedKey ? 'projects-col-pinned-last' : ''}`}
                    draggable={column.key !== 'actions'}
                    onDragStart={(e) => handleHeaderDragStart(e, column.key)}
                    onDragOver={(e) => handleHeaderDragOver(e, column.key)}
                    onDragEnd={handleHeaderDragEnd}
                  >
                    <div className="flex flex-col gap-2">
                      <div
                        className={`flex items-center text-sm font-medium text-gray-500 ${column.key === 'actions' ? 'justify-end' : 'justify-center cursor-pointer whitespace-nowrap'}`}
                        onClick={column.key !== 'actions' ? () => requestSort(column.key) : undefined}
                      >
                        <span className="truncate" title={column.label}>{column.label}</span>
                        {column.key !== 'actions' && (
                          <span className="ml-1 inline-flex w-4 shrink-0 items-center justify-center text-gray-500">
                            <SortIcon direction={sortIconDirection(sortConfig, column.key)} />
                          </span>
                        )}
                        {column.key !== 'actions' && (
                          <button
                            type="button"
                            data-pin-toggle
                            title={isPinned ? 'Unpin column' : 'Pin column to left'}
                            aria-label={isPinned ? `Unpin ${column.label}` : `Pin ${column.label}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTogglePin(column.key);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            draggable={false}
                            className={`projects-pin-toggle ml-1 p-0.5 rounded shrink-0 transition-opacity ${
                              isPinned
                                ? 'is-pinned text-red-600 hover:text-red-800 hover:bg-red-50'
                                : 'text-blue-500 hover:text-blue-700 hover:bg-blue-50'
                            }`}
                          >
                            <span className="inline-flex">
                              {isPinned ? (
                                <MdPushPin className="h-3.5 w-3.5" />
                              ) : (
                                <MdOutlinePushPin className="h-3.5 w-3.5" />
                              )}
                            </span>
                          </button>
                        )}
                      </div>
                      {column.key !== 'actions' && (
                        <div className="relative" style={{ overflow: 'visible', zIndex: 1 }}>
                          {visibleColumns.includes(column.key) && (
                            <>
                              {column.type === 'search' && (
                                <input
                                  type="text"
                                  placeholder="Search"
                                  className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light text-zinc-500"
                                  value={columnFilters[column.key] || ''}
                                  onChange={(e) => {
                                    setColumnFilters(prev => ({ ...prev, [column.key]: e.target.value }));
                                    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
                                    setHasMoreData(true);
                                  }}
                                />
                              )}

                              {column.type === 'select' && (
                                <div
                                  className="relative"
                                  ref={activeFilterColumn === column.filterKey ? filterDropdownRef : null}
                                  style={getFilterWrapperStyle(activeFilterColumn === column.filterKey)}
                                >
                                  <button
                                    onClick={(e) => toggleColumnFilter(column.filterKey, e)}
                                    className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light flex items-center justify-between ${columnFilterValues[column.filterKey]?.length > 0 ? 'text-red-600 border-red-500' : 'text-zinc-500'
                                      }`}
                                  >
                                    <span>
                                      {columnFilterValues[column.filterKey]?.length > 0
                                        ? `${columnFilterValues[column.filterKey].length} selected`
                                        : 'Filter'}
                                    </span>
                                    <FiFilter className="ml-2" />
                                  </button>
                                  {activeFilterColumn === column.filterKey &&
                                    renderFilterPopup(
                                      <ColumnFilterDropdown
                                        isOpen={activeFilterColumn === column.filterKey}
                                        options={getUniqueValues(column.key)}
                                        selectedValues={columnFilterValues[column.filterKey] || []}
                                        onSelect={(value) => handleColumnFilterSelect(column.filterKey, value)}
                                        onClose={closeFilterPopup}
                                        maxHeight="60vh"
                                      />
                                    )}
                                </div>
                              )}

                              {column.type === 'date' && (
                                <div
                                  className="relative"
                                  ref={activeFilterColumn === 'date' ? filterDropdownRef : null}
                                  style={getFilterWrapperStyle(activeFilterColumn === 'date')}
                                >
                                  <button
                                    onClick={(e) => toggleColumnFilter('date', e)}
                                    className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light flex items-center justify-between ${dateRange.start || dateRange.end ? 'text-red-600' : 'text-zinc-500'
                                      }`}
                                  >
                                    <span className="truncate">Filter</span>
                                    <FiFilter className="ml-2 shrink-0" />
                                  </button>
                                  {activeFilterColumn === 'date' &&
                                    renderFilterPopup(
                                      <DateRangeFilterDropdown
                                        isOpen={activeFilterColumn === 'date'}
                                        currentRange={dateRange}
                                        onApply={(range) => {
                                          setDateRange(range);
                                          setCurrentDisplayCount(INITIAL_LOAD_COUNT);
                                          setHasMoreData(true);
                                        }}
                                        onClose={closeFilterPopup}
                                      />
                                    )}
                                </div>
                              )}

                              {column.type === 'range' && (
                                <div
                                  className="relative"
                                  ref={activeFilterColumn === column.filterKey ? filterDropdownRef : null}
                                  style={getFilterWrapperStyle(activeFilterColumn === column.filterKey)}
                                >
                                  <button
                                    onClick={(e) => toggleColumnFilter(column.filterKey, e)}
                                    className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light flex items-center justify-between ${(
                                      (column.filterKey === 'takenToProject' && (takenToProjectRange.min !== null || takenToProjectRange.max !== null)) ||
                                      (column.filterKey === 'returnedFromProject' && (returnedFromProjectRange.min !== null || returnedFromProjectRange.max !== null)) ||
                                      (column.filterKey === 'takenToDate' && (takenToDateRange.min !== null || takenToDateRange.max !== null)) ||
                                      (column.filterKey === 'totalEstimated' && (totalEstimatedRange.min !== null || totalEstimatedRange.max !== null))
                                    )
                                      ? 'text-red-600'
                                      : 'text-zinc-500'
                                      }`}
                                  >
                                    <span>Filter</span>
                                    <FiFilter className="ml-2" />
                                  </button>
                                  {activeFilterColumn === column.filterKey &&
                                    renderFilterPopup(
                                      <RangeFilterDropdown
                                        isOpen={activeFilterColumn === column.filterKey}
                                        currentRange={
                                          column.filterKey === 'takenToProject'
                                            ? takenToProjectRange
                                            : column.filterKey === 'returnedFromProject'
                                              ? returnedFromProjectRange
                                              : column.filterKey === 'takenToDate'
                                                ? takenToDateRange
                                                : totalEstimatedRange
                                        }
                                        label={`Filter by ${column.label}`}
                                        minLabel="Min Quantity"
                                        maxLabel="Max Quantity"
                                        minPlaceholder="Min"
                                        maxPlaceholder="Max"
                                        onApply={(range) => {
                                          if (column.filterKey === 'takenToProject') {
                                            setTakenToProjectRange(range);
                                          } else if (column.filterKey === 'returnedFromProject') {
                                            setReturnedFromProjectRange(range);
                                          } else if (column.filterKey === 'takenToDate') {
                                            setTakenToDateRange(range);
                                          } else {
                                            setTotalEstimatedRange(range);
                                          }
                                          setCurrentDisplayCount(INITIAL_LOAD_COUNT);
                                          setHasMoreData(true);
                                        }}
                                        onClose={closeFilterPopup}
                                      />
                                    )}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={visibleTableColumns.length} className="p-0">
                  <div
                    className="projects-table-loading-inline"
                    style={hScroll.clientWidth > 0 ? { width: hScroll.clientWidth } : undefined}
                    role="status"
                    aria-live="polite"
                  >
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700" />
                    <span className="ml-2">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={visibleTableColumns.length} className="text-center py-4 text-red-700">
                  Failed to load inventory ledger: {error}
                </td>
              </tr>
            ) : displayedData.length === 0 ? (
              <tr>
                <td colSpan={visibleTableColumns.length} className="text-center py-4">No ledger transactions found</td>
              </tr>
            ) : (
              displayedData.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => setSelectedRowId(selectedRowId === item.id ? null : item.id)}
                  className={`hover:bg-gray-50 cursor-pointer ${selectedRowId === item.id ? 'bg-red-50' : ''}`}
                >
                  {visibleTableColumns.map((column) => {
                    const isPinned = pinnedVisibleKeys.includes(column.key);
                    if (column.key === 'actions') {
                      return (
                        <td
                          key="actions"
                          style={getColumnStyle(column)}
                          className="px-4 py-4 text-sm font-medium"
                        >
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={(e) => handleOpenEditModal(item, e)}
                              className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition"
                              title="Edit"
                            >
                              <FiEdit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => handleOpenDeleteConfirm(item.id, e)}
                              className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition"
                              title="Delete"
                            >
                              <FiTrash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      );
                    }

                    let cellValue = item[column.key];
                    if (column.key === 'updatedAtIso') {
                      const isCreatedSameAsUpdated = item.createdAtIso && item.updatedAtIso && (item.createdAtIso === item.updatedAtIso);
                      const isSavedByNameEmpty = !item.savedByName || !item.savedByName.trim();
                      if (isCreatedSameAsUpdated && isSavedByNameEmpty) {
                        cellValue = '-';
                      } else {
                        cellValue = formatFullTimestamp(item.updatedAtIso);
                      }
                    }

                    return (
                      <TooltipCell
                        key={column.key}
                        content={cellValue || '-'}
                        style={getColumnStyle(column)}
                        className={`text-gray-900 max-md:whitespace-normal break-words${
                          isPinned ? ' projects-col-pinned' : ''
                        }${column.key === lastPinnedKey ? ' projects-col-pinned-last' : ''}`}
                        align="left"
                      />
                    );
                  })}
                </tr>
              ))
            )}
            {!loading && !error && displayedData.length > 0 && hasMoreData && (
              <tr ref={loadingRef}>
                <td colSpan={visibleTableColumns.length} className="p-0">
                  <div
                    className="projects-table-loading-inline"
                    style={hScroll.clientWidth > 0 ? { width: hScroll.clientWidth } : undefined}
                  >
                    {loadingMore ? (
                      <>
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-700" />
                        <span className="ml-2">Loading more...</span>
                      </>
                    ) : (
                      <span className="text-gray-500">Scroll down to load more</span>
                    )}
                  </div>
                </td>
              </tr>
            )}
            {!loading && !error && displayedData.length > 0 && !hasMoreData && (
              <tr>
                <td colSpan={visibleTableColumns.length} className="p-0">
                  <div
                    className="projects-table-loading-inline"
                    style={hScroll.clientWidth > 0 ? { width: hScroll.clientWidth } : undefined}
                  >
                    <span className="text-gray-500">No more data to load</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {hScroll.overflow > 0 && (
        <div className="projects-h-scrollbar-rail">
          <div
            ref={hScrollbarRef}
            onScroll={handleHScrollbarScroll}
            className="projects-h-scrollbar"
            style={{
              marginLeft: `${pinnedColumnsWidth}px`,
              width: `${Math.max(hScroll.clientWidth - pinnedColumnsWidth, 0)}px`,
            }}
          >
            <div style={{ height: '1px', width: `${Math.max(hScroll.scrollWidth - pinnedColumnsWidth, 0)}px` }} />
          </div>
        </div>
      )}
      <RowsIndicator loadedCount={displayedData.length} totalCount={totalCount} />

      <InventoryLedgerModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        editingItem={editingItem}
        accounts={accounts}
        getAuthConfig={getAuthConfig}
        onSaved={() => fetchInventoryLedger(true)}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Transaction"
        message="Are you sure you want to delete this ledger transaction? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onClose={handleCloseDeleteConfirm}
        isLoading={isDeleting}
        isDestructive
      />
    </div>
  );

}

export default InventoryManagement;
